'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { CartItem, sizePriceAdder } from './menuData';

interface CartSheetProps {
  open: boolean;
  onClose: () => void;
  cart: CartItem[];
  onUpdateQuantity: (cartId: string, delta: number) => void;
  cartTotal: number;
  starsEarned: number;
}

export default function CartSheet({ open, onClose, cart, onUpdateQuantity, cartTotal, starsEarned }: CartSheetProps) {
  const [orderType, setOrderType] = useState<'gel-al' | 'masa'>('gel-al');
  const [placing, setPlacing] = useState(false);

  if (!open) return null;

  function placeOrder() {
    setPlacing(true);
    // Backend integration: POST /api/orders with cart items and orderType
    setTimeout(() => {
      setPlacing(false);
      onClose();
      toast.success('Siparişiniz alındı! 🎉', {
        description: `${starsEarned} Yıldız hesabınıza eklendi.`,
      });
    }, 1800);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-app bg-card rounded-t-3xl animate-slide-up max-h-[85vh] flex flex-col">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
          <h2 className="text-foreground font-bold text-lg">Sepetim</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Order type */}
        <div className="px-5 py-3 flex-shrink-0">
          <div className="flex gap-2 bg-muted p-1 rounded-xl">
            <button
              onClick={() => setOrderType('gel-al')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                orderType === 'gel-al' ? 'bg-white text-foreground card-shadow' : 'text-muted-foreground'
              }`}
            >
              🏃 Gel Al
            </button>
            <button
              onClick={() => setOrderType('masa')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                orderType === 'masa' ? 'bg-white text-foreground card-shadow' : 'text-muted-foreground'
              }`}
            >
              🪑 Masa Servisi
            </button>
          </div>
        </div>

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-5 space-y-3">
          {cart.map((item) => {
            const itemPrice = (item.product.price + sizePriceAdder[item.size]) * item.quantity;
            return (
              <div key={item.cartId} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <AppImage
                  src={item.product.image}
                  alt={item.product.alt}
                  width={56}
                  height={56}
                  className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-foreground font-semibold text-sm truncate">{item.product.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {item.size} · {item.milk}
                    {item.syrup !== 'Yok' && ` · ${item.syrup}`}
                    {item.extraShot && ' · Ekstra Shot'}
                  </p>
                  <p className="text-foreground font-bold text-sm mt-1">₺{itemPrice}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => onUpdateQuantity(item.cartId, -1)}
                    className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <Icon name="MinusIcon" size={14} className="text-foreground" />
                  </button>
                  <span className="text-foreground font-bold text-sm w-4 text-center font-tabular">{item.quantity}</span>
                  <button
                    onClick={() => onUpdateQuantity(item.cartId, 1)}
                    className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="PlusIcon" size={14} className="text-white" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="px-5 pt-3 pb-5 flex-shrink-0 border-t border-border">
          <div className="flex items-center justify-between mb-1">
            <span className="text-muted-foreground text-sm">Ara Toplam</span>
            <span className="text-foreground font-semibold font-tabular">₺{cartTotal}</span>
          </div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-muted-foreground text-sm">Kazanılacak Yıldız</span>
            <span className="text-gold font-bold flex items-center gap-1">
              <Icon name="StarIcon" size={14} variant="solid" className="text-gold" />
              {starsEarned} ★
            </span>
          </div>
          <button
            onClick={placeOrder}
            disabled={placing}
            className="w-full bg-primary text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors active:scale-[0.98] disabled:opacity-70"
          >
            {placing ? (
              <>
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span>Sipariş Veriliyor...</span>
              </>
            ) : (
              <>
                <span>Sipariş Ver</span>
                <span>— ₺{cartTotal}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}